import ClipboardManager from './ClipboardManager'
import EditPermissionManager from './Editpermissionmanager'
import { eventBus, EVENTS } from './EventBus'
import { ColumnManager } from './ColumnManager'
import { formatCode } from './formatCode'

// A column key (customer::branch::n) addresses the n-th entry of that
// customer/branch pair in distribution_result, counting placeholders. Removing
// an entry would shift every later column of the same pair onto the wrong
// allocation, so removal leaves a placeholder and insertion fills one.
const isSamePair = (dist, customerCode, branchCode) =>
  String(dist.customer_code) === String(customerCode) &&
  String(dist.branch_code ?? '') === String(branchCode ?? '')

const createColumnPlaceholder = (customerCode, branchCode) => ({
  _columnPlaceholder: true,
  customer_code: customerCode,
  branch_code: branchCode,
  delivery_quantity: '',
  original_delivery_quantity: '',
  user_edited: false,
  status: 6
})

export const removeColumnOccurrence = (
  distributionResult,
  customerCode,
  branchCode,
  index
) => {
  if (!Array.isArray(distributionResult)) return distributionResult
  let occurrence = -1
  return distributionResult.map((dist) => {
    if (!isSamePair(dist, customerCode, branchCode)) return dist
    occurrence += 1
    return occurrence === index
      ? createColumnPlaceholder(customerCode, branchCode)
      : dist
  })
}

const placeColumnOccurrence = (
  distributionResult,
  customerCode,
  branchCode,
  index,
  entry
) => {
  let occurrence = -1
  for (let i = 0; i < distributionResult.length; i++) {
    if (!isSamePair(distributionResult[i], customerCode, branchCode)) continue
    occurrence += 1
    if (occurrence === index) {
      // Only a placeholder slot is free; a real allocation already owns it.
      if (distributionResult[i]._columnPlaceholder) distributionResult[i] = entry
      return
    }
  }
  for (let n = occurrence + 1; n < index; n++) {
    distributionResult.push(createColumnPlaceholder(customerCode, branchCode))
  }
  distributionResult.push(entry)
}

export class DistributionManager {
  constructor(username = '不明') {
    this.username = username
    this.data = {}
    this.draftCells = {}
    this.changeLog = [] // {id, time, action, up, down}
    this.nextLogIndex = 0
    this.MAX_LOG_ENTRIES = 200

    this.originalItems = []
    this.permissionManager = new EditPermissionManager()

    this.clipboardManager = new ClipboardManager()

    this.resetCells = new Set()
  }

  setOriginalData(items) {
    this.originalItems = JSON.parse(JSON.stringify(items))

    // Reset state and add initial log entry
    this.data = {}
    this.draftCells = {}
    this.changeLog = []
    this.nextLogIndex = 0
    this.resetCells.clear()

    // Add initial log entry so user can rollback to initial state
    this.changeLog.push({
      id: nextLogId(),
      time: new Date().toLocaleString(),
      action: `[${this.username}] 元のデータバージョン`,
      changeData: {
        type: 'initial',
        itemCount: items.length
      },
      up: () => {
        this.data = {}
        this.draftCells = {}
        this.resetCells.clear()
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
      },
      down: () => {
        // Initial state - nothing to undo
      }
    })
    this.nextLogIndex = 1
  }

  reconcileItems(items) {
    const previousItems = this.originalItems || []
    const previousByRowKey = new Map(
      previousItems.map((item) => [
        this.getRowKey(item.hinmeiCode, item.niukabango),
        item
      ])
    )

    const nextItems = JSON.parse(JSON.stringify(items))

    nextItems.forEach((item) => {
      const previous = previousByRowKey.get(
        this.getRowKey(item.hinmeiCode, item.niukabango)
      )
      // React owns row metadata; this manager owns customer allocations,
      // including renamed/deleted columns. Merging the stale React allocations
      // by customer/branch resurrects the old column and doubles its quantity.
      if (previous && Array.isArray(previous.distribution_result)) {
        item.distribution_result = JSON.parse(
          JSON.stringify(previous.distribution_result)
        )
      }
    })

    return nextItems
  }

  syncItems(items) {
    this.originalItems = this.reconcileItems(items)
  }

  removeCustomerFromAllItems(columnKey) {
    const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
      columnKey
    )
      ? ColumnManager.parseColumnKey(columnKey)
      : { customerCode: columnKey, branchCode: '', index: 0 }

    // Remove from originalItems
    this.originalItems.forEach((item) => {
      if (item.distribution_result) {
        item.distribution_result = removeColumnOccurrence(
          item.distribution_result,
          customerCode,
          branchCode,
          index
        )
      }
    })

    // Remove from edited data
    Object.keys(this.data).forEach((rowKey) => {
      if (this.data[rowKey]) {
        delete this.data[rowKey][columnKey]
      }
    })

    // Remove from draftCells
    Object.keys(this.draftCells).forEach((cellKey) => {
      if (cellKey.endsWith(`::${columnKey}`)) {
        delete this.draftCells[cellKey]
      }
    })

    // Remove from resetCells
    const resetCellsArray = Array.from(this.resetCells)
    resetCellsArray.forEach((cellKey) => {
      if (cellKey.endsWith(`::${columnKey}`)) {
        this.resetCells.delete(cellKey)
      }
    })

    eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
  }

  removeCustomerFromItems(items, columnKey) {
    const targetRows = new Set(
      (items || []).map((item) =>
        this.getRowKey(item.hinmeiCode, item.niukabango)
      )
    )

    const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
      columnKey
    )
      ? ColumnManager.parseColumnKey(columnKey)
      : { customerCode: columnKey, branchCode: '', index: 0 }

    this.originalItems.forEach((item) => {
      const rowKey = this.getRowKey(item.hinmeiCode, item.niukabango)
      if (!targetRows.has(rowKey) || !item.distribution_result) return

      item.distribution_result = removeColumnOccurrence(
        item.distribution_result,
        customerCode,
        branchCode,
        index
      )
    })

    targetRows.forEach((rowKey) => {
      if (this.data[rowKey]) {
        delete this.data[rowKey][columnKey]
      }
    })

    Object.keys(this.draftCells).forEach((cellKey) => {
      const rowKey = cellKey.split('::').slice(0, 2).join('::')
      if (targetRows.has(rowKey) && cellKey.endsWith(`::${columnKey}`)) {
        delete this.draftCells[cellKey]
      }
    })

    Array.from(this.resetCells).forEach((cellKey) => {
      const rowKey = cellKey.split('::').slice(0, 2).join('::')
      if (targetRows.has(rowKey) && cellKey.endsWith(`::${columnKey}`)) {
        this.resetCells.delete(cellKey)
      }
    })

    eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
  }

  addCustomerToAllItems(columnKey, initialQuantity = 0) {
    const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
      columnKey
    )
      ? ColumnManager.parseColumnKey(columnKey)
      : { customerCode: columnKey, branchCode: '', index: 0 }

    this.originalItems.forEach((item) => {
      if (!item.distribution_result) item.distribution_result = []

      placeColumnOccurrence(item.distribution_result, customerCode, branchCode, index, {
        customer_code: customerCode,
        branch_code: branchCode,
        delivery_quantity: initialQuantity,
        original_delivery_quantity: '',
        user_edited: this.parseQuantity(initialQuantity) !== 0,
        status: 6
      })
    })
  }

  addCustomerToItems(items, columnKey, initialQuantity = 0) {
    const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
      columnKey
    )
      ? ColumnManager.parseColumnKey(columnKey)
      : { customerCode: columnKey, branchCode: '', index: 0 }

    const targetRows = new Set(
      (items || []).map((item) =>
        this.getRowKey(item.hinmeiCode, item.niukabango)
      )
    )

    this.originalItems.forEach((item) => {
      const rowKey = this.getRowKey(item.hinmeiCode, item.niukabango)
      if (!targetRows.has(rowKey)) return
      if (!item.distribution_result) item.distribution_result = []

      placeColumnOccurrence(item.distribution_result, customerCode, branchCode, index, {
        customer_code: customerCode,
        branch_code: branchCode,
        delivery_quantity: initialQuantity,
        original_delivery_quantity: '',
        user_edited: this.parseQuantity(initialQuantity) !== 0,
        status: 6
      })
    })
  }

  hasCustomerInItem(item, columnKey) {
    const rowKey = this.getRowKey(item.hinmeiCode, item.niukabango)
    const cellKey = this.getCellKey(item.hinmeiCode, item.niukabango, columnKey)

    if (this.data[rowKey] && columnKey in this.data[rowKey]) return true
    if (cellKey in this.draftCells) return true

    const realItem = this._getRealItem(item)
    const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
      columnKey
    )
      ? ColumnManager.parseColumnKey(columnKey)
      : { customerCode: columnKey, branchCode: '', index: 0 }

    const matching = (realItem?.distribution_result || []).filter(
      (d) =>
        String(d.customer_code) === String(customerCode) &&
        String(d.branch_code ?? '') === String(branchCode)
    )

    return Boolean(matching[index])
  }

  _getRealItem(item) {
    if (!item) return item
    // Search in originalItems for the item with matching keys
    const realItem = this.originalItems.find(
      (i) =>
        i.hinmeiCode === item.hinmeiCode &&
        String(i.niukabango) === String(item.niukabango)
    )
    return realItem || item
  }

  getOriginalValue(item, columnKey) {
    const realItem = this._getRealItem(item)
    if (!ColumnManager.isCompositeKey(columnKey)) {
      const dist = (realItem.distribution_result || []).find(
        (d) => String(d.customer_code) === String(columnKey)
      )
      return dist?.delivery_quantity ?? ''
    }

    const { customerCode, branchCode, index } =
      ColumnManager.parseColumnKey(columnKey)
    const dists = realItem.distribution_result || []

    const matching = dists.filter(
      (d) =>
        String(d.customer_code) === customerCode &&
        String(d.branch_code ?? '') === branchCode
    )

    return matching[index]?.delivery_quantity ?? ''
  }

  getOriginalStatus(item, columnKey) {
    const realItem = this._getRealItem(item)
    if (!ColumnManager.isCompositeKey(columnKey)) {
      const dist = (realItem.distribution_result || []).find(
        (d) => String(d.customer_code) === String(columnKey)
      )
      return dist?.status ?? 0
    }

    const { customerCode, branchCode, index } =
      ColumnManager.parseColumnKey(columnKey)
    const dists = realItem.distribution_result || []

    const matching = dists.filter(
      (d) =>
        String(d.customer_code) === customerCode &&
        String(d.branch_code ?? '') === branchCode
    )

    return matching[index]?.status ?? 0
  }

  returnNonStatus3ToRemain(items) {
    const historyData = []
    let totalReturned = 0

    items.forEach((item) => {
      const rowKey = this.getRowKey(item.hinmeiCode, item.niukabango)
      const allColumnKeys = new Set()

      if (item.distribution_result && Array.isArray(item.distribution_result)) {
        const pairCount = {}
        item.distribution_result.forEach((dist) => {
          const code = String(dist.customer_code ?? '')
          if (!code || code === 'price' || code === 'id') return
          const branchCode = String(dist.branch_code ?? '')
          const pairKey = `${code}::${branchCode}`
          const idx = pairCount[pairKey] ?? 0
          pairCount[pairKey] = idx + 1
          // Pads are not columns; an edit on one would turn it into an allocation.
          if (dist._columnPlaceholder) return
          allColumnKeys.add(ColumnManager.buildColumnKey(code, branchCode, idx))
        })
      }

      const edits = this.data[rowKey]
      if (edits) {
        Object.keys(edits).forEach((key) => {
          if (!key.startsWith('__fixed_') && key !== 'price' && key !== 'id') {
            allColumnKeys.add(key)
          }
        })
      }

      allColumnKeys.forEach((columnKey) => {
        const status = this.getOriginalStatus(item, columnKey)
        if (status !== 3) {
          const originalValue = this.getOriginalValue(item, columnKey)
          const currentValue = this.getValue(
            item.hinmeiCode,
            item.niukabango,
            columnKey,
            originalValue
          )
          const quantity = this.parseQuantity(currentValue)

          historyData.push({
            hinmeiCode: item.hinmeiCode,
            niukabango: item.niukabango,
            columnKey,
            beforeValue: currentValue,
            afterValue: null,
            beforeDraftValue:
              this.draftCells[
                this.getCellKey(item.hinmeiCode, item.niukabango, columnKey)
              ] ?? null
          })

          if (!this.data[rowKey]) this.data[rowKey] = {}
          this.data[rowKey][columnKey] = null

          const cellKey = this.getCellKey(
            item.hinmeiCode,
            item.niukabango,
            columnKey
          )
          this.resetCells.add(cellKey)
          this.clearDraftValue(item.hinmeiCode, item.niukabango, columnKey)

          totalReturned += quantity
        }
      })
    })

    if (historyData.length > 0) {
      this.addLogEntry({
        action: `[${this.username}] 荷割から ${totalReturned} 件を返却しました。`,
        changeData: historyData.map((data) => ({
          hinmeiCode: data.hinmeiCode,
          niukabango: data.niukabango,
          customerCode: data.columnKey,
          type: 'customer',
          originalValue: data.beforeValue,
          newValue: data.afterValue,
          changed: true
        })),
        up: () => {
          historyData.forEach((data) => {
            const { hinmeiCode, niukabango, columnKey } = data
            const rowKey = this.getRowKey(hinmeiCode, niukabango)
            if (!this.data[rowKey]) this.data[rowKey] = {}
            this.data[rowKey][columnKey] = null

            const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
            this.resetCells.add(cellKey)
            this.clearDraftValue(hinmeiCode, niukabango, columnKey)

            eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
              hinmeiCode,
              niukabango,
              customerCode: columnKey,
              value: '',
              changed: true
            })
          })
          emitChanged()
        },
        down: () => {
          historyData.forEach((data) => {
            const {
              hinmeiCode,
              niukabango,
              columnKey,
              beforeValue,
              beforeDraftValue
            } = data
            const rowKey = this.getRowKey(hinmeiCode, niukabango)
            if (!this.data[rowKey]) this.data[rowKey] = {}
            this.data[rowKey][columnKey] = beforeValue

            const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
            this.resetCells.delete(cellKey)

            if (beforeDraftValue !== null) {
              this.setDraftValue(
                hinmeiCode,
                niukabango,
                columnKey,
                beforeDraftValue
              )
            } else {
              this.clearDraftValue(hinmeiCode, niukabango, columnKey)
            }

            eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
              hinmeiCode,
              niukabango,
              customerCode: columnKey,
              value: beforeValue,
              changed: true
            })
          })
          emitChanged()
        }
      })

      function emitChanged() {
        eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
          action: 'return_non_status_3',
          totalReturned
        })
      }

      historyData.forEach((data) => {
        eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
          hinmeiCode: data.hinmeiCode,
          niukabango: data.niukabango,
          customerCode: data.columnKey,
          value: '',
          changed: true
        })
      })

      emitChanged()
    }

    return totalReturned
  }

  updateCustomerCode(oldCode, newCode, sourceOccurrenceIndex = null) {
    const { customerCode: newCustomerCode, branchCode: newBranchCode } =
      ColumnManager.isCompositeKey(newCode)
        ? ColumnManager.parseColumnKey(newCode)
        : { customerCode: newCode, branchCode: '' }

    const {
      customerCode: oldCustomerCode,
      branchCode: oldBranchCode,
      index: oldIndex
    } = ColumnManager.parseColumnKey(oldCode)

    this.originalItems.forEach((item) => {
      if (!item.distribution_result) return

      const targetIndex = sourceOccurrenceIndex ?? oldIndex
      let occurrence = -1
      const target = item.distribution_result.find((d) => {
        if (
          String(d.customer_code) !== String(oldCustomerCode) ||
          String(d.branch_code ?? '') !== String(oldBranchCode)
        ) {
          return false
        }
        occurrence += 1
        return occurrence === targetIndex
      })

      if (target) {
        const targetArrayIndex = item.distribution_result.indexOf(target)
        
        let hasSubsequent = false
        for (let i = targetArrayIndex + 1; i < item.distribution_result.length; i++) {
          const d = item.distribution_result[i]
          if (
            String(d.customer_code) === String(oldCustomerCode) &&
            String(d.branch_code ?? '') === String(oldBranchCode)
          ) {
            hasSubsequent = true
            break
          }
        }

        if (hasSubsequent) {
          item.distribution_result.splice(
            targetArrayIndex,
            1,
            createColumnPlaceholder(oldCustomerCode, oldBranchCode)
          )
        } else {
          item.distribution_result.splice(targetArrayIndex, 1)
        }

        const { index: newExpectedIndex } = ColumnManager.parseColumnKey(newCode)
        target.customer_code = newCustomerCode
        target.branch_code = newBranchCode

        let inserted = false
        let matchCount = 0

        for (let i = 0; i < item.distribution_result.length; i++) {
          const d = item.distribution_result[i]
          if (
            String(d.customer_code) === String(newCustomerCode) &&
            String(d.branch_code ?? '') === String(newBranchCode)
          ) {
            if (matchCount === newExpectedIndex) {
              item.distribution_result.splice(i, 1, target)
              inserted = true
              break
            }
            matchCount++
          }
        }

        if (!inserted) {
          const neededPads = newExpectedIndex - matchCount
          for (let i = 0; i < neededPads; i++) {
            item.distribution_result.push(
              createColumnPlaceholder(newCustomerCode, newBranchCode)
            )
          }
          item.distribution_result.push(target)
        }
      }
    })

    Object.keys(this.data).forEach((rowKey) => {
      const edits = this.data[rowKey]
      if (oldCode in edits) {
        edits[newCode] = edits[oldCode]
        delete edits[oldCode]
      }
    })

    const oldPrefix = `::${oldCode}`
    const newDraftCells = {}
    Object.keys(this.draftCells).forEach((cellKey) => {
      if (cellKey.endsWith(oldPrefix)) {
        const newCellKey = cellKey.slice(0, -oldPrefix.length) + `::${newCode}`
        newDraftCells[newCellKey] = this.draftCells[cellKey]
      } else {
        newDraftCells[cellKey] = this.draftCells[cellKey]
      }
    })
    this.draftCells = newDraftCells

    eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
      action: 'customer_code_updated',
      oldCode,
      newCode
    })
  }

  getCurrentData(columnManager = null, { forSave = false } = {}) {
    if (this.originalItems.length === 0) return []

    const currentItems = JSON.parse(JSON.stringify(this.originalItems))

    const displayLabelMap = new Map()
    if (columnManager?.displayLabels?.size > 0) {
      columnManager.displayLabels.forEach((label, key) => {
        const { customerCode } = ColumnManager.parseColumnKey(key)
        displayLabelMap.set(customerCode, label)
      })
    }

    currentItems.forEach((item) => {
      const rowKey = this.getRowKey(item.hinmeiCode, item.niukabango)
      const edits = this.data[rowKey]
      const distributionByColumn = new Map()
      const pairCounts = new Map()
      item.distribution_result = item.distribution_result || []
      item.distribution_result.forEach((dist) => {
        const pair = `${dist.customer_code}::${dist.branch_code ?? ''}`
        const index = pairCounts.get(pair) || 0
        pairCounts.set(pair, index + 1)
        distributionByColumn.set(
          ColumnManager.buildColumnKey(dist.customer_code, dist.branch_code, index),
          dist
        )
      })

      const applyCustomerValue = (columnKey, newValue) => {
        const markUserEdited = (dist) => {
          if (
            !Object.prototype.hasOwnProperty.call(
              dist,
              'original_delivery_quantity'
            )
          ) {
            dist.original_delivery_quantity = dist.delivery_quantity ?? ''
          }
          dist.delivery_quantity = newValue
          delete dist._columnPlaceholder
          dist.user_edited =
            this.parseQuantity(newValue) !==
            this.parseQuantity(dist.original_delivery_quantity)
        }

        const { customerCode, branchCode, index } =
          ColumnManager.parseColumnKey(columnKey)
        const lookupKey = ColumnManager.buildColumnKey(customerCode, branchCode, index)
        // updateCustomerCode already moves the source row to the visible key.
        // Historical _originalBranch/_originalIndex can now refer to a pad or
        // another column, especially after saving and reopening a draft.
        const dist = distributionByColumn.get(lookupKey)
        if (dist) {
          markUserEdited(dist)
        } else {
          const newDist = {
            customer_code: customerCode,
            branch_code: branchCode,
            delivery_quantity: newValue,
            original_delivery_quantity: '',
            user_edited: this.parseQuantity(newValue) !== 0,
            status: 6
          }
          item.distribution_result.push(newDist)
          distributionByColumn.set(lookupKey, newDist)
        }
      }

      if (edits) {
        if (!item.distribution_result) item.distribution_result = []

        Object.keys(edits).forEach((key) => {
          if (key.startsWith('__fixed_')) {
            const fieldName = key.replace('__fixed_', '')
            item[fieldName] = edits[key]
            item[key] = edits[key]
          } else {
            const newValue = edits[key]
            applyCustomerValue(key, newValue)
          }
        })
      }

      const draftPrefix = `${rowKey}::`
      Object.entries(this.draftCells).forEach(([cellKey, draftValue]) => {
        if (!cellKey.startsWith(draftPrefix)) return
        const columnKey = cellKey.slice(draftPrefix.length)
        if (!columnKey || columnKey === 'price' || columnKey === 'id') return
        if (columnKey.startsWith('__fixed_')) return
        if (!item.distribution_result) item.distribution_result = []
        applyCustomerValue(columnKey, draftValue)
      })

      if (columnManager) {
        const orderedResult = []
        const usedDistributions = new Set()

        columnManager.getCustomerColumns().forEach((col) => {
          const columnKey = col.customerCode
          const { customerCode, branchCode } = ColumnManager.parseColumnKey(columnKey)
          const fields = columnManager.customerHeaderFields?.get(columnKey)
          const lookupKey = ColumnManager.isCompositeKey(columnKey)
            ? columnKey
            : ColumnManager.buildColumnKey(customerCode, branchCode, 0)
          let dist = distributionByColumn.get(lookupKey)
          // A saved row needs a slot for every configured column. Otherwise a
          // later duplicate in a sparse row changes index when history opens.
          if (!dist && forSave) {
            dist = {
              customer_code: customerCode,
              branch_code: branchCode,
              delivery_quantity: '',
              original_delivery_quantity: '',
              user_edited: false,
              status: 6
            }
          }
          if (!dist) return
          usedDistributions.add(dist)
          delete dist._columnPlaceholder
          dist.branch_code = fields?.edaban ?? branchCode

          if (fields) {
            dist.location = fields.basho ?? dist.location ?? ''
            if ('torioiki' in fields && fields.torioiki !== undefined) {
              const torioikiValue =
                fields.torioiki === '●'
                  ? 1
                  : fields.torioiki === '＋1日'
                    ? 2
                    : fields.torioiki === '＋2日'
                      ? 3
                      : 0
              dist.reserve = torioikiValue
            }
          }
          orderedResult.push(dist)
        })

        // Pads only preserve occurrence indexes while editing/undoing. They
        // are not columns and must never become allocations in a saved draft.
        item.distribution_result.forEach((dist) => {
          if (!usedDistributions.has(dist) && !dist._columnPlaceholder) {
            orderedResult.push(dist)
          }
        })
        item.distribution_result = orderedResult
      }

      if (displayLabelMap.size > 0 && item.distribution_result) {
        item.distribution_result = item.distribution_result.map((d) => {
          const newLabel = displayLabelMap.get(String(d.customer_code))
          return newLabel ? { ...d, customer_code: newLabel } : d
        })
      }

      const calculatedProfit = this.calculateProfit(item)
      item.profit = calculatedProfit !== null ? calculatedProfit : ''
      item.remain_quantity = this.calculateRemaining(item)
    })

    return currentItems
  }

  getChanges() {
    const changes = []

    // Collect all customer changes from the active change log
    const activeLog = this.changeLog.slice(0, this.nextLogIndex)

    activeLog.forEach((logEntry) => {
      if (!logEntry.changeData) return

      if (Array.isArray(logEntry.changeData)) {
        logEntry.changeData.forEach((data) => {
          changes.push({ ...data, time: logEntry.time, logId: logEntry.id })
        })
      } else {
        changes.push({
          ...logEntry.changeData,
          time: logEntry.time,
          logId: logEntry.id
        })
      }
    })

    Object.keys(this.data).forEach((rowKey) => {
      const [hinmeiCode, niukabango] = rowKey.split('::')
      const edits = this.data[rowKey]
      const originalItem = this.originalItems.find(
        (item) =>
          item.hinmeiCode === hinmeiCode &&
          String(item.niukabango) === String(niukabango)
      )

      Object.keys(edits).forEach((key) => {
        if (key.startsWith('__fixed_')) {
          const fieldName = key.replace('__fixed_', '')
          const newValue = edits[key]
          const originalValue = originalItem?.[fieldName]
          if (originalValue !== newValue) {
            changes.push({
              hinmeiCode,
              niukabango,
              field: fieldName,
              type: 'fixed',
              originalValue,
              newValue,
              changed: true
            })
          }
        }
      })
    })

    return changes
  }

  updateFixedField(hinmeiCode, niukabango, field, value, force = false) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)
    if (!this.data[rowKey]) this.data[rowKey] = {}

    const fixedFieldKey = `__fixed_${field}`

    const allowZeroFixedFields = new Set([
      'quantity',
      'listing_quantity',
      'input_price',
      'output_price'
    ])
    const normalize = (v) => {
      if (v === undefined || v === null) return ''
      if (field === 'grade' || field === 'size') return String(v)
      if (!allowZeroFixedFields.has(field) && Number(v) === 0) return ''
      return v
    }
    const normalizedValue = normalize(value)

    const oldValue = this.data[rowKey][fixedFieldKey]
    if (!force && oldValue === normalizedValue) return

    this.data[rowKey][fixedFieldKey] = normalizedValue

    eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
      hinmeiCode,
      niukabango,
      field,
      value: normalizedValue,
      type: 'fixed'
    })
  }

  clearFixedField(hinmeiCode, niukabango, field, force = false) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)

    const emitReset = () => {
      eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
        hinmeiCode,
        niukabango,
        field,
        value: undefined,
        type: 'fixed'
      })
    }

    if (!this.data[rowKey]) {
      if (force) emitReset()
      return
    }

    const fixedFieldKey = `__fixed_${field}`
    if (!(fixedFieldKey in this.data[rowKey])) {
      if (force) emitReset()
      return
    }

    if (!force) return

    delete this.data[rowKey][fixedFieldKey]
    if (Object.keys(this.data[rowKey]).length === 0) {
      delete this.data[rowKey]
    }

    emitReset()
  }

  getFixedField(hinmeiCode, niukabango, field, originalValue) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)
    const fixedFieldKey = `__fixed_${field}`

    if (this.data[rowKey] && fixedFieldKey in this.data[rowKey]) {
      return this.data[rowKey][fixedFieldKey]
    }
    return originalValue
  }

  hasFixedField(hinmeiCode, niukabango, field) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)
    const fixedFieldKey = `__fixed_${field}`
    return Boolean(this.data[rowKey] && fixedFieldKey in this.data[rowKey])
  }

  getDataForSave(columnManager = null) {
    return {
      originalItems: this.originalItems,
      currentItems: this.getCurrentData(columnManager, { forSave: true }),
      changes: this.getChanges(),
      editsOnly: this.data,
      changeCount: this.getChanges().length,
      timestamp: new Date().toISOString(),
      changeLog: this.changeLog
    }
  }

  resetToOriginal() {
    const beforeResetData = {
      data: this.data,
      draftCells: this.draftCells,
      resetCells: new Set(this.resetCells)
    }

    this.data = {}
    this.draftCells = {}
    this.resetCells.clear()

    this.addLogEntry({
      action: `[${this.username}] すべての変更を元のデータにリセット`,
      up: () => {
        this.data = {}
        this.draftCells = {}
        this.resetCells.clear()
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, {})
      },
      down: () => {
        this.data = beforeResetData.data
        this.draftCells = beforeResetData.draftCells
        this.resetCells = beforeResetData.resetCells
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
      }
    })
    eventBus.emit(EVENTS.DISTRIBUTION_RESET, {})
  }

  setData(savedEdits) {
    this.data = savedEdits || {}
    eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
  }

  parseQuantity(value) {
    if (value === null || value === undefined) return 0
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0
    const s = String(value).trim()
    if (s === '') return 0
    const n = Number(s.replace(/,/g, ''))
    return Number.isFinite(n) ? n : 0
  }

  getCellKey(hinmeiCode, niukabango, columnKey) {
    return `${hinmeiCode}::${niukabango}::${columnKey}`
  }

  getRowKey(hinmeiCode, niukabango) {
    return `${hinmeiCode}::${niukabango}`
  }

  setDraftValue(hinmeiCode, niukabango, columnKey, value) {
    const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
    this.draftCells[cellKey] = value

    eventBus.emit(EVENTS.CELL_VALUE_CHANGED, {
      hinmeiCode,
      niukabango,
      customerCode: columnKey,
      value,
      isDraft: true
    })
  }

  clearDraftValue(hinmeiCode, niukabango, columnKey) {
    const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
    if (cellKey in this.draftCells) delete this.draftCells[cellKey]
  }

  commitChange(hinmeiCode, niukabango, columnKey, rawValue) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)

    const normalize = (v) => {
      const s = String(v ?? '').trim()
      if (s === '') return null
      const n = Number.parseInt(s, 10)
      return Number.isNaN(n) ? null : n
    }
    const finalValue = normalize(rawValue)

    if (!this.data[rowKey]) this.data[rowKey] = {}

    let committedValue
    if (columnKey in this.data[rowKey]) {
      committedValue = this.data[rowKey][columnKey]
    } else {
      const originalItem = this.originalItems.find(
        (i) =>
          i.hinmeiCode === hinmeiCode &&
          String(i.niukabango) === String(niukabango)
      )
      committedValue = normalize(
        this.getOriginalValue(originalItem || {}, columnKey)
      )
    }

    const hasChanged = committedValue !== finalValue
    if (!hasChanged) {
      this.clearDraftValue(hinmeiCode, niukabango, columnKey)
      return finalValue === null ? '' : finalValue
    }

    const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
    const beforeData = {
      value: committedValue,
      draftValue: this.draftCells[cellKey] ?? null
    }

    this.data[rowKey][columnKey] = finalValue
    this.clearDraftValue(hinmeiCode, niukabango, columnKey)

    const emitCommitted = (value) => {
      eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
        hinmeiCode,
        niukabango,
        customerCode: columnKey,
        value,
        changed: true
      })
      eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
        rowKey,
        data: this.data[rowKey]
      })
    }

    const customerCode = formatCode(columnKey)

    const baseNiukabango = String(niukabango).split('__')[0]

    this.addLogEntry({
      action: `[${this.username}] ${hinmeiCode}-${baseNiukabango} の ${customerCode} を ${
        finalValue === null ? '(空白)' : finalValue
      } に変更`,
      changeData: {
        hinmeiCode,
        niukabango,
        customerCode: columnKey,
        type: 'customer',
        originalValue: committedValue,
        newValue: finalValue,
        changed: true
      },
      up: () => {
        if (!this.data[rowKey]) this.data[rowKey] = {}
        this.data[rowKey][columnKey] = finalValue
        this.clearDraftValue(hinmeiCode, niukabango, columnKey)
        emitCommitted(finalValue)
      },
      down: () => {
        if (!this.data[rowKey]) this.data[rowKey] = {}
        this.data[rowKey][columnKey] = beforeData.value
        this.clearDraftValue(hinmeiCode, niukabango, columnKey)
        emitCommitted(beforeData.value)
      }
    })

    emitCommitted(finalValue)
    return finalValue === null ? '' : finalValue
  }

  calculateDistributed(realItem) {
    const rowKey = this.getRowKey(realItem.hinmeiCode, realItem.niukabango)
    const edited = this.data[rowKey] || {}

    const effectiveValues = {}

    const pairCount = {}
    ;(realItem.distribution_result || []).forEach((d) => {
      const code = String(d.customer_code ?? '')
      if (!code || code === 'price' || code === 'id') return
      const branchCode = String(d.branch_code ?? '')
      const pairKey = `${code}::${branchCode}`
      const idx = pairCount[pairKey] ?? 0
      pairCount[pairKey] = idx + 1
      const columnKey = ColumnManager.buildColumnKey(code, branchCode, idx)
      effectiveValues[columnKey] = d.delivery_quantity
    })

    Object.keys(edited).forEach((key) => {
      if (!key.startsWith('__fixed_')) {
        effectiveValues[key] = edited[key]
      }
    })

    const prefix = `${realItem.hinmeiCode}::${realItem.niukabango}::`
    Object.entries(this.draftCells).forEach(([cellKey, v]) => {
      if (!cellKey.startsWith(prefix)) return
      const columnKey = cellKey.slice(prefix.length)
      if (!columnKey || columnKey === 'price' || columnKey === 'id') return
      effectiveValues[columnKey] = v
    })

    return Object.values(effectiveValues).reduce(
      (sum, v) => sum + this.parseQuantity(v),
      0
    )
  }

  calculateRemaining(item) {
    if (
      item.price_groups &&
      Array.isArray(item.price_groups) &&
      !String(item.niukabango).includes('__p_')
    ) {
      let initialQty = 0
      let totalDistributed = 0

      item.price_groups.forEach((p) => {
        const proxy = {
          ...item,
          ...p,
          niukabango: `${item.niukabango}__p_${p._priceId}`
        }
        const realProxy = this._getRealItem(proxy)
        const qtyUnit = this.parseQuantity(
          this.getFixedField(
            realProxy.hinmeiCode,
            realProxy.niukabango,
            'quantity_unit',
            realProxy.quantity_unit
          )
        )

        initialQty += this.parseQuantity(
          this.getFixedField(
            realProxy.hinmeiCode,
            realProxy.niukabango,
            'quantity',
            realProxy.quantity ??
              realProxy.total_quantity ??
              realProxy.totalQuantity ??
              0
          )
        )
        const distributed = this.calculateDistributed(realProxy)
        if (qtyUnit > 0) {
          totalDistributed += distributed * qtyUnit
        } else {
          totalDistributed += distributed
        }
      })

      return initialQty - totalDistributed
    }

    const realItem = this._getRealItem(item)
    const quantityUnit = this.parseQuantity(
      this.getFixedField(
        realItem.hinmeiCode,
        realItem.niukabango,
        'quantity_unit',
        realItem.quantity_unit
      )
    )
    const initialQty = this.parseQuantity(
      this.getFixedField(
        realItem.hinmeiCode,
        realItem.niukabango,
        'quantity',
        realItem.quantity ??
          realItem.total_quantity ??
          realItem.totalQuantity ??
          0
      )
    )

    if (quantityUnit > 0) {
      const totalX = this.calculateDistributed(realItem)
      return initialQty - totalX * quantityUnit
    } else {
      return initialQty - this.calculateDistributed(realItem)
    }
  }

  calculateProfit(item) {
    if (
      item.price_groups &&
      Array.isArray(item.price_groups) &&
      !String(item.niukabango).includes('__p_')
    ) {
      let hasProfit = false
      const totalProfit = item.price_groups.reduce((sum, p) => {
        const proxy = {
          ...item,
          ...p,
          niukabango: `${item.niukabango}__p_${p._priceId}`
        }
        const profit = this.calculateProfit(proxy)
        if (profit !== null) hasProfit = true
        return sum + (profit ?? 0)
      }, 0)
      return hasProfit ? totalProfit : null
    }

    const realItem = this._getRealItem(item)
    const hasFixedOutputPrice =
      this.hasFixedField(
        realItem.hinmeiCode,
        realItem.niukabango,
        'output_price'
      ) ||
      Object.prototype.hasOwnProperty.call(realItem, '__fixed_output_price')
    const hasFixedInputPrice =
      this.hasFixedField(
        realItem.hinmeiCode,
        realItem.niukabango,
        'input_price'
      ) || Object.prototype.hasOwnProperty.call(realItem, '__fixed_input_price')
    const outputPriceRaw = this.getFixedField(
      realItem.hinmeiCode,
      realItem.niukabango,
      'output_price',
      realItem.output_price ?? ''
    )
    const inputPriceRaw = this.getFixedField(
      realItem.hinmeiCode,
      realItem.niukabango,
      'input_price',
      realItem.input_price ?? ''
    )
    const outputBlank =
      outputPriceRaw === '' ||
      outputPriceRaw === null ||
      outputPriceRaw === undefined ||
      (!hasFixedOutputPrice && Number(outputPriceRaw) === 0)
    const inputBlank =
      inputPriceRaw === '' ||
      inputPriceRaw === null ||
      inputPriceRaw === undefined ||
      (!hasFixedInputPrice && Number(inputPriceRaw) === 0)

    if (inputBlank || outputBlank) return null

    const outputPrice = this.parseQuantity(outputPriceRaw)
    const inputPrice = this.parseQuantity(inputPriceRaw)

    const distributed = this.calculateDistributed(realItem)
    const profit = (outputPrice - inputPrice) * distributed
    return Object.is(profit, -0) ? 0 : profit
  }

  calculateTotalProfit(items) {
    if (!items || items.length === 0) return 0
    return items.reduce((sum, item) => {
      const profit = this.calculateProfit(item)
      return sum + (profit ?? 0)
    }, 0)
  }

  getValue(hinmeiCode, niukabango, columnKey, originalValue) {
    const rowKey = this.getRowKey(hinmeiCode, niukabango)
    const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)

    if (cellKey in this.draftCells) return this.draftCells[cellKey]

    if (this.data[rowKey] && columnKey in this.data[rowKey]) {
      const edited = this.data[rowKey][columnKey]
      return edited === null ? '' : edited
    }

    return originalValue ?? ''
  }

  isCellReset(hinmeiCode, niukabango, columnKey) {
    const cellKey = this.getCellKey(hinmeiCode, niukabango, columnKey)
    return this.resetCells.has(cellKey)
  }

  addLogEntry(entry) {
    if (typeof entry.up !== 'function' || typeof entry.down !== 'function') {
      console.warn("Expected 'up' and 'down' functions in log entry.", entry)
    }
    if (this.nextLogIndex < this.changeLog.length) {
      // If we are not at the end of the log, discard future entries
      this.changeLog = this.changeLog.slice(0, this.nextLogIndex)
    }

    this.changeLog.push({
      id: nextLogId(),
      time: new Date().toLocaleString(),
      ...entry
    })
    this.nextLogIndex = this.changeLog.length

    if (this.changeLog.length > this.MAX_LOG_ENTRIES) {
      this.changeLog = this.changeLog.slice(-this.MAX_LOG_ENTRIES)
      this.nextLogIndex = this.changeLog.length
    }

    eventBus.emit(EVENTS.LOG_ENTRY_ADDED, entry)
  }

  clearLog() {
    this.changeLog = []
    this.nextLogIndex = 0
    eventBus.emit(EVENTS.LOG_CLEARED)
  }

  changeLogUndo(steps = 1) {
    for (let i = 0; i < steps; i++) {
      if (!this.isChangeLogUndoable()) break
      this.nextLogIndex -= 1
      const logEntry = this.changeLog[this.nextLogIndex]
      if (logEntry && typeof logEntry.down === 'function') {
        logEntry.down()
        eventBus.emit(EVENTS.LOG_UNDO, logEntry)
      }
    }
  }

  changeLogRedo(steps = 1) {
    for (let i = 0; i < steps; i++) {
      if (!this.isChangeLogRedoable()) break
      const logEntry = this.changeLog[this.nextLogIndex]
      this.nextLogIndex += 1
      if (logEntry && typeof logEntry.up === 'function') {
        logEntry.up()
        eventBus.emit(EVENTS.LOG_REDO, logEntry)
      }
    }
  }

  rollbackToLogEntry(targetIndex = 0) {
    // 1. Rollback from current position down to target
    while (this.nextLogIndex - 1 > targetIndex) {
      this.nextLogIndex -= 1
      const logEntry = this.changeLog[this.nextLogIndex]
      if (logEntry && typeof logEntry.down === 'function') logEntry.down()
    }

    // 2. Or Roll forward if target is ahead of current
    while (
      this.nextLogIndex - 1 < targetIndex &&
      this.nextLogIndex < this.changeLog.length
    ) {
      const logEntry = this.changeLog[this.nextLogIndex]
      if (logEntry && typeof logEntry.up === 'function') logEntry.up()
      this.nextLogIndex += 1
    }

    eventBus.emit(EVENTS.DISTRIBUTION_RESET, this.data)
  }

  isChangeLogUndoable() {
    const previousEntry = this.changeLog[this.nextLogIndex - 1]
    // The initial entry establishes the baseline; it is not a user change.
    // A first entry created after clearLog() has no such marker and remains
    // undoable.
    return (
      this.nextLogIndex > 0 && previousEntry?.changeData?.type !== 'initial'
    )
  }
  isChangeLogRedoable() {
    return this.nextLogIndex < this.changeLog.length
  }

  getData() {
    return this.data
  }
  getLog() {
    return this.changeLog
  }

  autoAllocateRemaining(items, columnKey, options = { skipLog: false }) {
    let totalAllocated = 0

    const historyData = []

    items.forEach((item) => {
      const remaining = this.calculateRemaining(item)
      if (remaining <= 0) return

      const targetItem =
        item.price_groups &&
        item.price_groups.length > 0 &&
        !String(item.niukabango).includes('__p_')
          ? {
              ...item,
              ...item.price_groups[0],
              niukabango: `${item.niukabango}__p_${item.price_groups[0]._priceId}`
            }
          : item

      const realTargetItem = this._getRealItem(targetItem)

      const quantityUnit = this.getFixedField(
        realTargetItem.hinmeiCode,
        realTargetItem.niukabango,
        'quantity_unit',
        realTargetItem.quantity_unit ?? 0
      )

      // Calculate allocation amount:
      // - If quantity_unit exists: allocation = remain / quantity_unit
      // - If quantity_unit doesn't exist: allocation = remain
      const qtyUnitValue = this.parseQuantity(quantityUnit)
      const allocationAmount =
        qtyUnitValue > 0 ? Math.floor(remaining / qtyUnitValue) : remaining

      const rowKey = this.getRowKey(
        realTargetItem.hinmeiCode,
        realTargetItem.niukabango
      )
      const originalValue = this.getOriginalValue(realTargetItem, columnKey)
      const currentValue = this.getValue(
        realTargetItem.hinmeiCode,
        realTargetItem.niukabango,
        columnKey,
        originalValue
      )
      const newValue = this.parseQuantity(currentValue) + allocationAmount

      if (!this.data[rowKey]) this.data[rowKey] = {}

      const cellKey = this.getCellKey(
        realTargetItem.hinmeiCode,
        realTargetItem.niukabango,
        columnKey
      )

      historyData.push({
        cellKey,
        beforeValue: currentValue,
        afterValue: newValue,
        beforeDraftValue: this.draftCells[cellKey] ?? null,
        hinmeiCode: realTargetItem.hinmeiCode,
        niukabango: realTargetItem.niukabango,
        columnKey
      })

      this.data[rowKey][columnKey] = newValue
      this.clearDraftValue(
        realTargetItem.hinmeiCode,
        realTargetItem.niukabango,
        columnKey
      )
      totalAllocated += allocationAmount
    })

    if (totalAllocated > 0) {
      const actionPayload = {
        action: `[${this.username}] 3170-SP に自動で ${totalAllocated} ユニットを割り当て`,
        changeData: historyData.map((data) => ({
          hinmeiCode: data.hinmeiCode,
          niukabango: data.niukabango,
          customerCode: data.columnKey,
          type: 'customer',
          originalValue: data.beforeValue,
          newValue: data.afterValue,
          changed: true
        })),
        up: () => {
          historyData.forEach((data) => {
            const { hinmeiCode, niukabango, columnKey: ck, afterValue } = data
            const rk = this.getRowKey(hinmeiCode, niukabango)
            if (!this.data[rk]) this.data[rk] = {}
            this.data[rk][ck] = afterValue
            this.clearDraftValue(hinmeiCode, niukabango, ck)
            eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
              hinmeiCode,
              niukabango,
              customerCode: ck,
              value: afterValue,
              changed: true
            })
          })
          emitChanged()
        },
        down: () => {
          historyData.forEach((data) => {
            const {
              hinmeiCode,
              niukabango,
              columnKey: ck,
              beforeValue,
              beforeDraftValue
            } = data
            const rk = this.getRowKey(hinmeiCode, niukabango)
            if (!this.data[rk]) this.data[rk] = {}
            this.data[rk][ck] = beforeValue
            if (beforeDraftValue !== null) {
              this.setDraftValue(hinmeiCode, niukabango, ck, beforeDraftValue)
            } else {
              this.clearDraftValue(hinmeiCode, niukabango, ck)
            }
            eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
              hinmeiCode,
              niukabango,
              customerCode: ck,
              value: beforeValue,
              changed: true
            })
          })
          emitChanged()
        }
      }

      function emitChanged() {
        eventBus.emit(EVENTS.DISTRIBUTION_CHANGED, {
          columnKey,
          totalAllocated
        })
      }

      if (!options.skipLog) {
        this.addLogEntry(actionPayload)
      }

      requestAnimationFrame(() => {
        historyData.forEach((data) => {
          eventBus.emit(EVENTS.DISTRIBUTION_COMMITTED, {
            hinmeiCode: String(data.hinmeiCode),
            niukabango: String(data.niukabango),
            customerCode: String(data.columnKey),
            value: data.afterValue,
            changed: true
          })
        })
        emitChanged()
      })

      if (options.skipLog) {
        return { totalAllocated, actionPayload }
      }
    }

    if (options.skipLog) {
      return { totalAllocated, actionPayload: null }
    }
    return totalAllocated
  }

  validate(items) {
    const errors = []
    items.forEach((item) => {
      const remaining = this.calculateRemaining(item)
      if (remaining !== 0) {
        errors.push({
          hinmeiCode: item.hinmeiCode,
          niukabango: item.niukabango,
          remaining
        })
      }
    })
    return { isValid: errors.length === 0, errors }
  }

  hasRemainingQuantity(items) {
    return items.some((item) => this.calculateRemaining(item) !== 0)
  }

  hasMissingCustomerCode(items, columnManager) {
    if (!columnManager) return false

    const isMissingCode = (code) => {
      const value = String(code ?? '').trim()
      return !value || value.startsWith('NEW_')
    }

    const hasUnnamedColumn = columnManager.getCustomerColumns().some((col) => {
      const { customerCode } = ColumnManager.parseColumnKey(col.customerCode)
      return (
        (isMissingCode(customerCode) || !String(col.label ?? '').trim()) &&
        items.some((item) => this.hasCustomerInItem(item, col.customerCode))
      )
    })
    if (hasUnnamedColumn) return true

    return this.getCurrentData(columnManager).some((item) =>
      (item.distribution_result || []).some(
        (dist) =>
          isMissingCode(dist.customer_code) &&
          this.parseQuantity(dist.delivery_quantity) !== 0
      )
    )
  }

  canSave(items, columnManager) {
    return (
      !this.hasRemainingQuantity(items) &&
      !this.hasMissingCustomerCode(items, columnManager)
    )
  }

  reset() {
    this.data = {}
    this.draftCells = {}
    this.changeLog = []
    this.nextLogIndex = 0
    this.resetCells.clear()
    eventBus.emit(EVENTS.DISTRIBUTION_RESET, {})
  }
}

export default DistributionManager

let logIdCounter = 0
function nextLogId() {
  return `log-${Date.now()}-${logIdCounter++}`
}
